import { Component } from 'react';
import { get, flowRight as compose } from 'lodash';
import PropTypes from 'prop-types';
import { graphql } from '@apollo/client/react/hoc';
import {
  MUTATION_UPDATE_FEATURE_VIEW,
  MUTATION_UPDATE_FEATURE_SORT_BY,
  MUTATION_UPDATE_FEATURE_SEARCH,
  MUTATION_RESET_FEATURE_SEARCH,
  QUERY_FEATURE_CRITERIA,
  MUTATION_UPDATE_FEATURE_FLAG
} from './gql';
import { ReactChildrenPropType } from '../../../../lib/commonPropTypes';

const updateToggle = (toggle) => {
  localStorage.setItem('feature.toggle', toggle);
};

class FeatureFlagContainer extends Component {
  constructor() {
    super();

    this.getSetting = this.getSetting.bind(this);
    this.resetSearch = this.resetSearch.bind(this);
    this.updatePagination = this.updatePagination.bind(this);
    this.updateSearch = this.updateSearch.bind(this);
    this.updateSortBy = this.updateSortBy.bind(this);
    this.updateView = this.updateView.bind(this);
    this.activateFeatureFlag = this.activateFeatureFlag.bind(this);
  }

  getSetting(setting, fallback) {
    const { [setting]: existing } = this.props;

    const storage = localStorage.getItem(`feature.${setting}`);

    return storage || existing || fallback;
  }

  resetSearch = () => {
    const { resetSearch } = this.props;

    return resetSearch();
  };

  updatePagination = (pagination) => {
    const existing = JSON.parse(this.getSetting('pagination', '{}'));

    localStorage.setItem('feature.pagination', JSON.stringify({ ...existing, ...pagination }));
  };

  updateSearch = (search) => {
    const { updateSearch } = this.props;

    localStorage.setItem('feature.search', search);

    return updateSearch({ variables: { search } });
  };

  updateSortBy = (sortBy) => {
    const { updateSortBy } = this.props;

    localStorage.setItem('feature.sortBy', sortBy);

    return updateSortBy({ variables: { sortBy } });
  };

  updateView = (view) => {
    const { updateView } = this.props;
    return updateView({ variables: { view } });
  };

  activateFeatureFlag = (featureId, active) => {
    const { activateFeatureFlag } = this.props;
    return activateFeatureFlag({
      variables: {
        featureFlagId: featureId,
        active
      }
    });
  };

  buildProps = () => ({
    pagination: JSON.parse(this.getSetting('pagination', '{}')),
    search: this.getSetting('search'),
    sortBy: this.getSetting('sortBy'),
    toggle: this.getSetting('toggle'),
    view: this.getSetting('view'),
    resetSearch: this.resetSearch,
    updatePagination: this.updatePagination,
    updateSearch: this.updateSearch,
    updateSortBy: this.updateSortBy,
    updateToggle,
    updateView: this.updateView,
    activateFeatureFlag: this.activateFeatureFlag
  });

  render() {
    const { children, ...otherProps } = this.props;
    return children(this.buildProps(otherProps));
  }
}

FeatureFlagContainer.propTypes = {
  children: ReactChildrenPropType.isRequired,
  resetSearch: PropTypes.func.isRequired,
  updateSearch: PropTypes.func.isRequired,
  updateSortBy: PropTypes.func.isRequired,
  updateView: PropTypes.func.isRequired,
  pagination: PropTypes.string,
  search: PropTypes.string.isRequired,
  sortBy: PropTypes.string.isRequired,
  toggle: PropTypes.string,
  view: PropTypes.string.isRequired,
  activateFeatureFlag: PropTypes.func.isRequired
};

FeatureFlagContainer.defaultProps = {
  pagination: '{}',
  toggle: undefined
};

const FeatureContainerWithMutations = compose(
  graphql(MUTATION_UPDATE_FEATURE_VIEW, { name: 'updateView' }),
  graphql(MUTATION_UPDATE_FEATURE_SORT_BY, { name: 'updateSortBy' }),
  graphql(MUTATION_UPDATE_FEATURE_SEARCH, { name: 'updateSearch' }),
  graphql(MUTATION_RESET_FEATURE_SEARCH, { name: 'resetSearch' }),
  graphql(MUTATION_UPDATE_FEATURE_FLAG, { name: 'activateFeatureFlag' }),
  graphql(QUERY_FEATURE_CRITERIA, {
    options: () => ({
      fetchPolicy: 'cache-first',
      ssr: false // Authentication only works client side
    }),
    props: ({ data: { featureCriteria } }) => ({
      pagination: get(featureCriteria, 'paginationSupplier', '{}'),
      search: get(featureCriteria, 'searchSupplier', ''),
      sortBy: get(featureCriteria, 'sortBySupplier', ''),
      toggle: get(featureCriteria, 'toggleSupplier', ''),
      view: get(featureCriteria, 'viewSupplier', '')
    })
  })
)(FeatureFlagContainer);

export default FeatureContainerWithMutations;
